import React, {
  useState,
  useImperativeHandle,
  createContext,
  useContext,
  useMemo,
  useId,
  useLayoutEffect,
  useEffect
} from "react";
import styled, { css, ThemeProvider } from "styled-components";
import PropTypes from "prop-types";
import { NavLink } from "react-router-dom";
import { space, layout } from "styled-system";
import { themeGet } from "@styled-system/theme-get";
import { commonKeys } from "../../hooks/keypress";
import useActionMenu from "./useActionMenu";
import {
  crossFadeIn,
  crossFadeOut,
  beforeDotCollapsing,
  beforeDotExpanding,
  afterDotCollapsing,
  afterDotExpanding,
  beforeCrossExpanding,
  beforeCrossCollapsing,
  afterCrossExpanding,
  afterCrossCollapsing
} from "./ActionsMenu.animations";
import { FloatingFocusManager, FloatingPortal, useMergeRefs } from "@floating-ui/react";
import { getFloatingUiRootElement, getFloatingUiZIndex } from "../../utils/floatingUiHelpers";

const ActionMenuContext = createContext({});

const StyledActionsMenuContainer = styled.div`
  pointer-events: none;
  opacity: 0;
  visibility: hidden;

  &.hack-for-legacy-tests {
    position: absolute;
    pointer-events: none;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    clip-path: inset(50%);
    white-space: nowrap;
    border-width: 0;
  }

  &.visible {
    visibility: visible;
    opacity: 1;
    pointer-events: auto;
  }
`;

const Wrapper = styled.div`
  ${space}
  ${layout}
  position: relative;
  width: auto;
`;

const Control = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  -moz-appearance: none;
  -webkit-appearance: none;
  appearance: none;
  box-shadow: none;
  text-decoration: none;
  text-align: center;
  border-radius: ${(props) => themeGet("radii.2")(props)};
  transition: ${(props) => themeGet("transition.transitionDefault")(props)};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  width: ${(props) => (props.variant === "circle" ? "35px" : "30px")};
  height: ${(props) => (props.variant === "circle" ? "35px" : "30px")};
  border-radius: ${(props) => (props.variant === "circle" ? "50%" : themeGet("radii.2")(props))};
  border: ${(props) => (props.variant === "circle" ? "solid 2px" : "solid 1px")};

  /* Default variant styles */
  ${(props) =>
    props.variant === "default" &&
    css`
      background-color: ${props.disabled ? themeGet("colors.greyLighter")(props) : "white"};
      border-color: ${props.disabled
        ? themeGet("colors.greyLighter")(props)
        : themeGet("colors.greyLight")(props)};
    `}

  /* Circle variant styles */
  ${(props) =>
    props.variant === "circle" &&
    css`
      background-color: ${props.disabled
        ? themeGet("colors.greyLighter")(props)
        : themeGet("colors.greyDarkest")(props)};
      border-color: ${props.disabled
        ? themeGet("colors.greyLighter")(props)
        : themeGet("colors.greyDarkest")(props)};
    `}

  &:hover,
  &:focus {
    outline: 0;
    border-color: ${(props) =>
      props.disabled ? themeGet("colors.greyLighter")(props) : themeGet("colors.primary")(props)};
    ${(props) =>
      !props.disabled &&
      props.variant === "default" &&
      css`
        background-color: ${themeGet("colors.greyLightest")(props)};
      `}
  }

  &[data-state="open"] .actionsMenu__dots {
    &::before {
      animation: 500ms ${beforeDotCollapsing} cubic-bezier(0.68, -0.6, 0.32, 1.6) forwards;
    }
    &::after {
      animation: 500ms ${afterDotCollapsing} cubic-bezier(0.68, -0.6, 0.32, 1.6) forwards;
    }
  }
  &[data-state="open"] .actionsMenu__cross {
    animation: 500ms ${crossFadeIn} ease-in-out forwards;
    &::before {
      animation: 500ms ${beforeCrossExpanding} cubic-bezier(0.68, -0.6, 0.32, 1.6) forwards;
    }
    &::after {
      animation: 500ms ${afterCrossExpanding} cubic-bezier(0.68, -0.6, 0.32, 1.6) forwards;
    }
  }
  &[data-state="closed"] .actionsMenu__dots {
    &::before {
      animation: 500ms ${beforeDotExpanding} cubic-bezier(0.68, -0.6, 0.32, 1.6) forwards;
    }
    &::after {
      animation: 500ms ${afterDotExpanding} cubic-bezier(0.68, -0.6, 0.32, 1.6) forwards;
    }
  }
  &[data-state="closed"] .actionsMenu__cross {
    animation: none;
    opacity: 0;
    &::before {
      animation: 500ms ${beforeCrossCollapsing} cubic-bezier(0.68, -0.6, 0.32, 1.6) forwards;
    }
    &::after {
      animation: 500ms ${afterCrossCollapsing} cubic-bezier(0.68, -0.6, 0.32, 1.6) forwards;
    }
  }
  &[data-state="closed"][data-has-been-open="true"] .actionsMenu__cross {
    animation: 500ms ${crossFadeOut} ease-in-out forwards;
  }
`;

const DOT_SIZE = 4;
const DOT_OFFSET = 7; // Distance from center dot to top/bottom dots

const Dots = styled.div`
  position: relative;
  border-radius: 50%;
  height: ${DOT_SIZE}px;
  width: ${DOT_SIZE}px;
  background-color: ${(props) =>
    props.variant === "circle"
      ? themeGet("colors.white")(props)
      : themeGet("colors.greyDarker")(props)};
  /* Force GPU rendering for consistent anti-aliasing across all dots */
  transform: translateZ(0);
  &::before,
  &::after {
    content: "";
    display: block;
    position: absolute;
    left: 0;
    top: 0;
    border-radius: 50%;
    height: ${DOT_SIZE}px;
    width: ${DOT_SIZE}px;
    background-color: ${(props) =>
      props.variant === "circle"
        ? themeGet("colors.white")(props)
        : themeGet("colors.greyDarker")(props)};
  }
  &::before {
    transform: translateY(-${DOT_OFFSET}px);
  }
  &::after {
    transform: translateY(${DOT_OFFSET}px);
  }
`;

const Cross = styled.div`
  opacity: 0;
  position: absolute;
  left: calc(50% - 2px);
  top: calc(50% - 2px);
  pointer-events: none;
  &::before,
  &::after {
    content: "";
    display: block;
    position: absolute;
    border-radius: 2px;
    height: 4px;
    width: 4px;
    background-color: ${(props) =>
      props.variant === "circle"
        ? themeGet("colors.white")(props)
        : themeGet("colors.greyDarker")(props)};
  }
  &::before {
    transform: rotate(-45deg);
  }
  &::after {
    transform: rotate(45deg);
  }
`;

const Menu = styled.div`
  display: block;
  width: ${(props) => (props.menuWidth ? props.menuWidth : "auto")};
  z-index: 5;
  background-color: ${themeGet("colors.white")};
  border: 1px solid ${themeGet("colors.greyLight")};
  box-shadow: ${themeGet("shadows.boxDefault")};
  border-radius: ${(props) => themeGet("radii.2")(props)};
`;

export const ActionsMenuHeading = styled((props) => {
  const { actionMenu } = useContext(ActionMenuContext);
  const id = useId();

  // // Only sets `aria-labelledby` on the Popover root element
  // // if this component is mounted inside it.
  useLayoutEffect(() => {
    actionMenu?.setLabelId?.(id);

    return () => actionMenu?.setLabelId?.(undefined);
  }, [id, actionMenu]);

  return (
    <div
      {...props}
      onKeyUp={(e) => {
        if (e.key === commonKeys.ENTER && props?.canClick) {
          props?.onClick();
        }
      }}
    />
  );
}).attrs({
  tabIndex: "0",
  role: "button"
})`
  color: ${(props) => themeGet("colors.greyDark")(props)};
  padding: 8px;
  width: 100%;
  font-size: ${(props) => themeGet("fontSizes.0")(props)};
  font-weight: ${(props) => themeGet("fontWeights.1")(props)};
  border-bottom: solid 1px ${(props) => themeGet("colors.greyLighter")(props)};
  white-space: nowrap;
  cursor: ${(props) => (props.canClick ? "pointer" : "default")};
`;

const ActionMenuItemStyles = css`
  white-space: nowrap;
  display: block;
  width: 100%;
  text-align: left;
  cursor: pointer;
  margin: 0;
  padding: 8px;
  appearance: none;
  background-color: ${(props) =>
    props.selected ? themeGet("colors.success20")(props) : "transparent"};
  border: none;
  border-bottom: solid 1px ${themeGet("colors.white")};
  border-radius: 0;
  color: ${themeGet("colors.greyDarkest")};
  font-size: ${themeGet("fontSizes.0")};
  line-height: ${themeGet("fontSizes.0")};
  font-family: ${themeGet("fonts.main")};
  font-weight: ${themeGet("fontWeights.2")};
  text-decoration: none;
  transition: ${themeGet("transition.transitionDefault")};
  &:hover {
    background-color: ${themeGet("colors.primaryLightest")};
  }
  &:first-child {
    border-radius: ${themeGet("radii.2")} ${themeGet("radii.2")} 0 0;
  }
  &:last-child {
    border: 0;
    border-radius: 0 0 ${themeGet("radii.2")} ${themeGet("radii.2")};
  }
  &:only-child {
    border-radius: ${themeGet("radii.2")};
  }
  &#other {
    padding: 6px 8px;
  }
`;

const StyledNavLink = styled(NavLink)`
  text-decoration: none;
`;

export const ActionsMenuItem = styled((props) => {
  const { id, onItemClick, actionMenu } = useContext(ActionMenuContext);

  const { as, ...others } = props;

  const Component = as ? as : others.href ? "a" : "button";
  const disabled = props.disabled;

  let newProps = {
    ...others,
    ...(actionMenu?.getItemProps?.() || {})
  };

  const { onClick: originalOnClick } = newProps;

  const onClick = useMemo(
    () => (e) => {
      onItemClick?.(e);
      originalOnClick?.(e);
    },
    [originalOnClick, onItemClick]
  );

  if (props.to) {
    const LinkedComponent = props.Component || Component;

    return (
      <StyledNavLink to={props.to}>
        <LinkedComponent {...newProps} />
      </StyledNavLink>
    );
  }

  if (Component === "button") {
    newProps = {
      ...others,
      type: "button",
      ["data-action-menu-id"]: id
    };
  }

  if (props.Component)
    return <props.Component {...newProps} onClick={onClick} disabled={disabled} />;

  return <Component {...newProps} onClick={onClick} disabled={disabled} />;
}).attrs({ role: "menuitem" })`
  ${({ Component }) => (Component ? "" : ActionMenuItemStyles)}
`;

export const ActionsMenuBody = ({
  theme,
  onToggle,
  toggleState,
  // direction - Deprecated
  direction = "right-start",
  menuWidth,
  customTriggerComponent,
  renderTrigger,
  children,
  ariaLabel = "Options Menu",
  onTriggerFocus,
  closeMenu,
  closeOnClick = false,
  "data-testid": dataTestId = "ActionsMenu",
  variant = "default",
  disabled = false,
  ...props
}) => {
  const id = useId();
  const [hasBeenOpen, setHasBeenOpen] = useState(false);

  const actionMenu = useActionMenu({
    placement: direction,
    open: toggleState,
    onOpenChange: (_, e) => {
      if (e) {
        onToggle?.(e);
      }
    }
  });

  useEffect(() => {
    if (actionMenu.open) {
      setHasBeenOpen(true);
    }
  }, [actionMenu.open]);

  const childrenRef = children.ref;
  const triggerRef = useMergeRefs([actionMenu.refs.setReference, childrenRef]);
  const ref = useMergeRefs([actionMenu.refs.setFloating]);

  const triggerProps = useMemo(
    () => ({
      "aria-label": ariaLabel,
      onFocus: onTriggerFocus,
      id,
      disabled,
      ...actionMenu.getReferenceProps({
        ...props,
        onClick: disabled ? undefined : onToggle,
        ref: triggerRef,
        "data-state": actionMenu.open ? "open" : "closed",
        "data-has-been-open": hasBeenOpen ? "true" : undefined,
        "data-testid": dataTestId
      })
    }),
    [
      ariaLabel,
      onTriggerFocus,
      id,
      actionMenu,
      onToggle,
      props,
      triggerRef,
      dataTestId,
      disabled,
      hasBeenOpen
    ]
  );

  let triggerComponent = (
    <Control {...triggerProps} variant={variant} disabled={disabled}>
      <Dots className="actionsMenu__dots" variant={variant} />
      <Cross className="actionsMenu__cross" variant={variant} />
    </Control>
  );

  if (renderTrigger) {
    triggerComponent = renderTrigger(triggerProps);
  }

  if (customTriggerComponent) {
    triggerComponent = (
      <div role="button" {...triggerProps}>
        {customTriggerComponent}
      </div>
    );
  }

  const value = useMemo(
    () => ({
      id,
      onItemClick: (e) => {
        if (closeOnClick) {
          closeMenu(e);
        }
      },
      actionMenu
    }),
    [closeOnClick, closeMenu, id, actionMenu]
  );

  const style = useMemo(
    () => ({
      ...actionMenu.floatingStyles,
      zIndex: getFloatingUiZIndex(actionMenu.refs.reference)
    }),
    [actionMenu.floatingStyles, actionMenu.refs.reference]
  );

  const menuDataTestId = useMemo(() => `${dataTestId}__menu`, [dataTestId]);

  const { getFloatingProps } = actionMenu;

  const floatingProps = useMemo(() => getFloatingProps(props), [getFloatingProps, props]);

  const component = (
    <ActionMenuContext.Provider value={value}>
      <Wrapper {...props}>
        {triggerComponent}

        {toggleState ? (
          <FloatingPortal root={getFloatingUiRootElement(actionMenu.refs.reference)}>
            <FloatingFocusManager context={actionMenu.context} modal={true}>
              <StyledActionsMenuContainer
                aria-labelledby={actionMenu.labelId}
                data-testid={menuDataTestId}
                {...floatingProps}
                style={style}
                className="actionMenu-content visible"
                aria-hidden="false"
                ref={ref}
              >
                <Menu menuWidth={menuWidth} isOpen={toggleState}>
                  {children}
                </Menu>
              </StyledActionsMenuContainer>
            </FloatingFocusManager>
          </FloatingPortal>
        ) : (
          /* * HACK: Fixing all the broken tests in teamform-app-ui is too
          time consuming * right this moment with a lot of the tests asserting
          against ActionsMenu items. * Rendering the markup even when closed
          but in a hidden state ensures that tests pass. * Ideally, we would
          update all the tests in teamform-app-ui to open the ActionsMenu *
          before assertion. **/
          <StyledActionsMenuContainer
            aria-labelledby={actionMenu.labelId}
            data-testid={menuDataTestId}
            className="actionMenu-content hack-for-legacy-tests"
          >
            <Menu menuWidth={menuWidth} isOpen={toggleState}>
              {children}
            </Menu>
          </StyledActionsMenuContainer>
        )}
      </Wrapper>
    </ActionMenuContext.Provider>
  );

  return theme ? <ThemeProvider theme={theme}>{component}</ThemeProvider> : component;
};

ActionsMenuBody.propTypes = {
  onTriggerFocus: PropTypes.func,
  onToggle: PropTypes.func.isRequired,
  closeMenu: PropTypes.func.isRequired,
  toggleState: PropTypes.bool.isRequired,
  closeOnClick: PropTypes.bool,
  direction: PropTypes.string,
  placement: PropTypes.string,
  menuTopPosition: PropTypes.string,
  menuLeftPosition: PropTypes.string,
  menuRightPosition: PropTypes.string,
  menuWidth: PropTypes.string,
  customTriggerComponent: PropTypes.node,
  renderTrigger: PropTypes.func,
  "data-testid": PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  theme: PropTypes.object,
  ariaLabel: PropTypes.string,
  variant: PropTypes.oneOf(["default", "circle"]),
  disabled: PropTypes.bool
};

const ActionsMenu = React.forwardRef((props, ref) => {
  const [toggleState, setToggle] = useState(false);

  const onToggle = (e) => {
    e.stopPropagation();
    setToggle(!toggleState);
  };

  useImperativeHandle(ref, () => ({
    closeMenu: () => {
      setToggle(false);
    }
  }));

  return (
    <ActionsMenuBody
      {...props}
      closeMenu={() => setToggle(false)}
      toggleState={toggleState}
      onToggle={onToggle}
    >
      {props.children}
    </ActionsMenuBody>
  );
});

ActionsMenu.propTypes = {
  isOpen: PropTypes.bool,
  direction: PropTypes.oneOf([
    "left",
    "right",
    "top",
    "bottom",
    "top-start",
    "top-end",
    "bottom-start",
    "bottom-end",
    "left-start",
    "left-end",
    "right-start",
    "right-end"
  ]),
  customTriggerComponent: PropTypes.node,
  renderTrigger: PropTypes.func,
  closeOnClick: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  /** Specifies the colour theme */
  theme: PropTypes.object,
  /** Specifies the aria-label for the button */
  ariaLabel: PropTypes.string,
  /** Specifies the variant of the ActionsMenu */
  variant: PropTypes.oneOf(["default", "circle"]),
  /** Specifies whether the ActionsMenu is disabled */
  disabled: PropTypes.bool
};

export default ActionsMenu;
